import React, { useRef, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { useMutation } from "@tanstack/react-query";
import { toast } from "sonner";
import { Loader2, Phone, ShieldCheck } from "lucide-react";
import { sharedApi } from "@/lib/api";
import Seo from "@/components/common/Seo";

/**
 * Iter 755 — Add phone screen. Accounts without a mobile on file (Google
 * SSO sign-ups, legacy imports) are routed here by the global 428
 * `phone_required` handler and the post-login `needs_phone` gate.
 * Flow: POST /auth/phone/request {phone} → POST /auth/phone/confirm
 * {phone, code}. Preview env may return needs_code:false (SMS off) —
 * the number is saved server-side and we return immediately.
 */
export default function AddPhonePage() {
  const nav = useNavigate();
  const [params] = useSearchParams();
  const next = params.get("next") || "/";
  const [phone, setPhone] = useState("");
  const [code, setCode] = useState("");
  const [step, setStep] = useState("phone"); // "phone" | "code"
  const [masked, setMasked] = useState("");
  const codeRef = useRef(null);

  const finish = () => {
    // Clear the local needs_phone flag so the gate stops redirecting.
    try {
      const raw = localStorage.getItem("allsale_user");
      if (raw && raw !== "undefined" && raw !== "null") {
        const u = JSON.parse(raw);
        u.needs_phone = false;
        u.phone = phone.trim();
        localStorage.setItem("allsale_user", JSON.stringify(u));
      }
    } catch { /* non-fatal */ }
    // Hard navigation re-hydrates AuthProvider from localStorage.
    window.location.assign(next);
  };

  const requestMut = useMutation({
    mutationFn: async () => (await sharedApi.post("/auth/phone/request", { phone: phone.trim() })).data,
    onSuccess: (data) => {
      if (data?.needs_code === false) {
        toast.success("Mobile number saved");
        finish();
        return;
      }
      setMasked(data?.masked_phone || "");
      setStep("code");
      setTimeout(() => codeRef.current?.focus(), 30);
    },
    onError: (err) => {
      const detail = err?.response?.data?.detail;
      toast.error(typeof detail === "string" ? detail : "Couldn't send the code — check the number and try again.");
    },
  });

  const confirmMut = useMutation({
    mutationFn: async () => (await sharedApi.post("/auth/phone/confirm", { phone: phone.trim(), code })).data,
    onSuccess: (data) => {
      if (data?.verified) {
        toast.success("Mobile number verified");
        // WO 801c — welcome bonus releases once e-mail + mobile verified.
        if (data?.welcome_bonus_released) toast.success("🎉 100 welcome points added");
        finish();
      } else {
        toast.error("Verification didn't complete — please try again.");
      }
    },
    onError: (err) => {
      const detail = err?.response?.data?.detail;
      toast.error(typeof detail === "string" ? detail : "Wrong or expired code.");
    },
  });

  return (
    <div className="max-w-md mx-auto px-4 md:px-8 py-12" data-testid="add-phone-page">
      <Seo title="Add your mobile number" />
      <div className="rounded-3xl bg-white shadow-card p-8">
        <div className="w-12 h-12 rounded-2xl bg-[color:var(--brand-blue-wash)] text-[color:var(--brand-blue)] grid place-items-center">
          <ShieldCheck className="w-6 h-6" />
        </div>
        <h1 className="mt-4 font-display text-2xl font-bold text-slate-900">Add your mobile number</h1>
        <p className="mt-2 text-sm text-slate-500 leading-relaxed">
          Allsale Classifieds is New Zealand-only — we verify every member with a NZ mobile number.
        </p>

        {step === "phone" ? (
          <form
            className="mt-6 space-y-4"
            onSubmit={(e) => { e.preventDefault(); if (!requestMut.isPending) requestMut.mutate(); }}
          >
            <label className="block">
              <span className="text-xs font-bold uppercase tracking-[0.14em] text-slate-400 mb-1.5 block">
                NZ mobile number <span className="text-red-500">*</span>
              </span>
              <div className="relative">
                <Phone className="w-4 h-4 text-slate-400 absolute left-3 top-1/2 -translate-y-1/2" />
                <input
                  data-testid="add-phone-input"
                  value={phone}
                  onChange={(e) => setPhone(e.target.value.replace(/[^0-9+ ]/g, "").slice(0, 20))}
                  type="tel"
                  inputMode="tel"
                  required
                  autoFocus
                  placeholder="021 123 4567"
                  className="w-full pl-10 pr-3 py-2.5 rounded-xl bg-slate-50 border border-soft focus:border-[color:var(--brand-blue)] focus:outline-none text-sm"
                />
              </div>
              <span className="mt-1 block text-[11px] text-slate-400">Starts with 02 or +64 2.</span>
            </label>
            <button
              type="submit"
              disabled={!phone.trim() || requestMut.isPending}
              data-testid="add-phone-send-code"
              className="w-full inline-flex items-center justify-center gap-2 rounded-full bg-[color:var(--brand-blue)] hover:bg-[color:var(--brand-blue-hover)] disabled:opacity-50 text-white font-bold py-3 transition-colors"
            >
              {requestMut.isPending ? <Loader2 className="w-4 h-4 animate-spin" /> : null}
              Send code
            </button>
          </form>
        ) : (
          <form
            className="mt-6 space-y-4"
            onSubmit={(e) => { e.preventDefault(); if (!confirmMut.isPending) confirmMut.mutate(); }}
          >
            <p className="text-xs text-slate-500">Enter the 6-digit code we texted to {masked || phone}.</p>
            <input
              ref={codeRef}
              data-testid="add-phone-code"
              value={code}
              onChange={(e) => setCode(e.target.value.replace(/[^0-9]/g, "").slice(0, 6))}
              inputMode="numeric"
              autoComplete="one-time-code"
              placeholder="123456"
              required
              className="w-full rounded-xl border border-soft bg-white py-2.5 px-3 text-lg font-mono tracking-widest text-center outline-none focus:border-[color:var(--brand-blue)]"
            />
            <div className="flex items-center gap-2">
              <button
                type="button"
                onClick={() => { setCode(""); setStep("phone"); }}
                data-testid="add-phone-back"
                className="rounded-full border border-soft bg-white hover:bg-slate-50 text-slate-700 font-semibold px-4 py-2 text-xs"
              >
                Change number
              </button>
              <button
                type="submit"
                disabled={code.length < 6 || confirmMut.isPending}
                data-testid="add-phone-confirm"
                className="flex-1 inline-flex items-center justify-center gap-2 rounded-full bg-[color:var(--brand-blue)] hover:bg-[color:var(--brand-blue-hover)] disabled:opacity-50 text-white font-bold py-2.5 text-sm transition-colors"
              >
                {confirmMut.isPending ? <Loader2 className="w-4 h-4 animate-spin" /> : null}
                Verify
              </button>
            </div>
          </form>
        )}
      </div>
    </div>
  );
}
